import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm, type UseFormRegister } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createLocation, getAreas, getLocation, getLocations, getRegions, updateLocation } from '@/api/locations';
import { getCodeBoard } from '@/api/baseplanCodes';
import { ApiError } from '@/api/client';
import {
  austPostLabels, austPostValues, branchCodesFor, codeDerivedFields, locationSchema, locationTypeLabels, locationTypes, toOptionalId, toParentLocationId,
  type LocationFormData,
} from './schema';
import type { LocationWrite } from '@/types/oscar';

const inputCls = 'mt-1 w-full rounded border border-border bg-white px-2 py-1.5 text-sm';

const empty: LocationFormData = {
  LocationDesc: '', LocationType: 'Branch', BranchCode: '', OtherBranchCodes: [], ParentLocationId: null, RegionId: null, AreaId: null,
  OU: '', AdOffice: '',
  PhysicalStreet: '', PhysicalCity: '', PhysicalState: '', PhysicalPostcode: '', PhysicalCountry: '',
  PostalStreet: '', PostOfficeBox: '', PostalCity: '', PostalState: '', PostalPostcode: '', PostalCountry: '',
  IsBranch: true, IsMinor: false, IsStockLocation: true, Active: true, Notes: '',
  Phone: '', AustPostDelivers: '', AddressNotes: '',
};

export function LocationEditForm() {
  const { id } = useParams();
  const locationId = id ? Number(id) : null;
  const isEdit = locationId !== null;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const regionsQuery = useQuery({ queryKey: ['locations', 'regions'], queryFn: ({ signal }) => getRegions(signal), retry: false });
  const areasQuery = useQuery({ queryKey: ['locations', 'areas'], queryFn: ({ signal }) => getAreas(undefined, signal), retry: false });
  const locationQuery = useQuery({ queryKey: ['location', locationId], queryFn: ({ signal }) => getLocation(locationId!, signal), enabled: isEdit });
  // Only top-level rows can be a parent — sub-locations are one level deep, so a
  // location that is already a child never appears here.
  const sitesQuery = useQuery({ queryKey: ['locations', 'top-level'], queryFn: ({ signal }) => getLocations({ parent: 'none', activeOnly: true }, signal), retry: false });
  // The reporting hierarchy (same key as the Reporting branches board, so they share a cache).
  const codesQuery = useQuery({ queryKey: ['locations', 'codes'], queryFn: ({ signal }) => getCodeBoard(signal), retry: false });
  // Every location, to say when a branch picked here is another location's primary.
  const allLocationsQuery = useQuery({ queryKey: ['locations', 'list', 'all'], queryFn: ({ signal }) => getLocations({}, signal), retry: false });

  const { register, handleSubmit, reset, watch, setValue, formState } = useForm<LocationFormData>({ resolver: zodResolver(locationSchema), defaultValues: empty });
  const selectedRegion = watch('RegionId');
  const selectedParentId = watch('ParentLocationId');
  const selectedCode = watch('BranchCode');
  // A code from the hierarchy decides area, region and the branch / minor flags.
  const derived = codeDerivedFields(selectedCode, codesQuery.data);

  useEffect(() => {
    if (locationQuery.data) {
      const l = locationQuery.data.Location;
      reset({
        LocationDesc: l.LocationDesc,
        LocationType: l.LocationType,
        BranchCode: l.BranchCode ?? '',
        OtherBranchCodes: (l.BranchCodes ?? []).filter((c) => c !== (l.BranchCode ?? '')),
        // ⛔ Normalised on the way IN as well. The API's 0 means "no parent",
        // and feeding it straight into a field the schema requires to be
        // positive is what produced "Number must be greater than 0" on a
        // location that correctly showed "— top-level site".
        ParentLocationId: toParentLocationId(l.ParentLocationId),
        RegionId: l.RegionId,
        AreaId: l.AreaId,
        OU: l.OU ?? '',
        AdOffice: l.AdOffice ?? '',
        PhysicalStreet: l.PhysicalStreet ?? '',
        PhysicalCity: l.PhysicalCity ?? '',
        PhysicalState: l.PhysicalState ?? '',
        PhysicalPostcode: l.PhysicalPostcode ?? '',
        PhysicalCountry: l.PhysicalCountry ?? '',
        PostalStreet: l.PostalStreet ?? '',
        PostOfficeBox: l.PostOfficeBox ?? '',
        PostalCity: l.PostalCity ?? '',
        PostalState: l.PostalState ?? '',
        PostalPostcode: l.PostalPostcode ?? '',
        PostalCountry: l.PostalCountry ?? '',
        IsBranch: l.IsBranch,
        IsMinor: l.IsMinor,
        IsStockLocation: l.IsStockLocation,
        Active: l.Active,
        Notes: l.Notes ?? '',
        Phone: l.Phone ?? '',
        AustPostDelivers: l.AustPostDelivers ?? '',
        AddressNotes: l.AddressNotes ?? '',
      });
    }
  }, [locationQuery.data, reset]);

  function toPayload(form: LocationFormData): LocationWrite {
    const blank = (v?: string) => (v && v.trim() !== '' ? v.trim() : null);
    // What the screen shows for a hierarchy code is what is saved - also for a location
    // loaded with an area that no longer matches its code's.
    const fromCode = codeDerivedFields(form.BranchCode, codesQuery.data);
    // Without a code the region is the chosen area's (a region with no area is kept).
    const areaOf = (areasQuery.data ?? []).find((a) => a.AreaId === form.AreaId);
    const d = fromCode
      ? { ...form, AreaId: fromCode.AreaId, RegionId: fromCode.RegionId, IsBranch: fromCode.IsBranch, IsMinor: fromCode.IsMinor }
      : areaOf ? { ...form, RegionId: areaOf.RegionId } : form;
    return {
      LocationDesc: d.LocationDesc.trim(),
      LocationType: d.LocationType,
      BranchCode: blank(d.BranchCode),
      // Every branch at the location, the primary first (the API keeps them in oscar.LocationCode).
      BranchCodes: branchCodesFor(d.BranchCode, d.OtherBranchCodes),
      // 0 is the API's "detach from parent" signal; null would mean "unchanged".
      ParentLocationId: d.ParentLocationId ?? 0,
      RegionId: d.RegionId ?? null,
      AreaId: d.AreaId ?? null,
      OU: blank(d.OU),
      AdOffice: blank(d.AdOffice),
      PhysicalStreet: blank(d.PhysicalStreet),
      PhysicalCity: blank(d.PhysicalCity),
      PhysicalState: blank(d.PhysicalState),
      PhysicalPostcode: blank(d.PhysicalPostcode),
      PhysicalCountry: blank(d.PhysicalCountry),
      PostalStreet: blank(d.PostalStreet),
      PostOfficeBox: blank(d.PostOfficeBox),
      PostalCity: blank(d.PostalCity),
      PostalState: blank(d.PostalState),
      PostalPostcode: blank(d.PostalPostcode),
      PostalCountry: blank(d.PostalCountry),
      IsBranch: d.IsBranch,
      IsMinor: d.IsMinor,
      IsStockLocation: d.IsStockLocation,
      Active: d.Active,
      Notes: blank(d.Notes),
      // '' clears on the API; null would mean "unchanged".
      Phone: d.Phone?.trim() ?? '',
      AustPostDelivers: d.AustPostDelivers,
      AddressNotes: d.AddressNotes?.trim() ?? '',
    };
  }

  const mutation = useMutation({
    mutationFn: (d: LocationFormData) => (isEdit ? updateLocation(locationId!, toPayload(d)) : createLocation(toPayload(d))),
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      queryClient.invalidateQueries({ queryKey: ['location', saved.LocationId] });
      navigate('/locations');
    },
    onError: (err) => setSubmitError(err instanceof ApiError ? err.message : 'Could not save the location.'),
  });

  // A location that already has children can't become a child itself (that would
  // be a third level), and nothing can be its own parent.
  const hasChildren = (locationQuery.data?.Children.length ?? 0) > 0;
  const parentOptions = (sitesQuery.data ?? []).filter((l) => l.LocationId !== locationId);
  const selectedParent = parentOptions.find((l) => l.LocationId === selectedParentId) ?? null;

  const regions = regionsQuery.data ?? [];
  // No reporting branch: the AREA is picked and the region follows it from the
  // Region → Area hierarchy (Anthony, 25/10/2026). Areas are grouped by region and
  // each option names its region too, so the closed box still shows it.
  const allAreas = areasQuery.data ?? [];
  const regionName = (id: number | null | undefined) => regions.find((r) => r.RegionId === id)?.RegionDesc ?? null;
  const areaGroups = [
    ...regions.map((r) => ({ label: r.RegionDesc, areas: allAreas.filter((a) => a.RegionId === r.RegionId) })),
    { label: 'No region', areas: allAreas.filter((a) => !regions.some((r) => r.RegionId === a.RegionId)) },
  ].filter((g) => g.areas.length > 0);
  const selectedArea = watch('AreaId');
  const areaRegion = regions.find((r) => r.RegionId === allAreas.find((a) => a.AreaId === selectedArea)?.RegionId);
  // A location saved before this rule with a region but no area keeps that region until an area is picked.
  const legacyRegion = selectedArea == null ? regions.find((r) => r.RegionId === selectedRegion) : undefined;
  function onPickArea(value: string) {
    const area = allAreas.find((a) => a.AreaId === toOptionalId(value));
    setValue('RegionId', area ? area.RegionId : null, { shouldDirty: true });
  }

  // Picking a code (by hand) brings its hierarchy with it; a blank name takes the code's.
  function onPickCode(code: string) {
    // A code made primary is no longer one of the others.
    const others = watch('OtherBranchCodes') ?? [];
    if (others.includes(code)) setValue('OtherBranchCodes', others.filter((c) => c !== code), { shouldDirty: true });
    const d = codeDerivedFields(code, codesQuery.data);
    if (!d) return;
    const opts = { shouldDirty: true };
    setValue('RegionId', d.RegionId, opts);
    setValue('AreaId', d.AreaId, opts);
    setValue('IsBranch', d.IsBranch, opts);
    setValue('IsMinor', d.IsMinor, opts);
    if (!watch('LocationDesc')?.trim()) setValue('LocationDesc', d.Description, opts);
  }

  // /locations/new?code=1203 - "Add location" from a reporting branch that has none.
  const prefilled = useRef(false);
  const codeParam = searchParams.get('code');
  useEffect(() => {
    if (isEdit || prefilled.current || !codeParam || !codesQuery.data) return;
    prefilled.current = true;
    if (!codesQuery.data.Codes.some((c) => c.Code === codeParam)) return;
    setValue('BranchCode', codeParam, { shouldDirty: true });
    onPickCode(codeParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, codeParam, codesQuery.data]);

  // Codes grouped by area, with a legacy value (not in the hierarchy) kept selectable.
  const board = codesQuery.data;
  const codeGroups = (board?.Areas ?? [])
    .map((a) => ({
      label: `${a.AreaDesc} (${board?.Regions.find((r) => r.RegionId === a.RegionId)?.RegionDesc ?? 'no region'})`,
      codes: (board?.Codes ?? []).filter((c) => c.AreaId === a.AreaId && (c.IsActive || c.Code === selectedCode)),
    }))
    .filter((g) => g.codes.length > 0);
  const unallocatedCodes = (board?.Codes ?? []).filter((c) => c.AreaId === null && (c.IsActive || c.Code === selectedCode));
  const legacyCode = selectedCode && board && !board.Codes.some((c) => c.Code === selectedCode) ? selectedCode : null;
  const derivedArea = derived ? allAreas.find((a) => a.AreaId === derived.AreaId) : undefined;
  const derivedRegion = derived ? regions.find((r) => r.RegionId === derived.RegionId) : undefined;

  // The other branches at this location.
  const otherCodes = watch('OtherBranchCodes') ?? [];
  const describe = (code: string) => board?.Codes.find((c) => c.Code === code)?.Description ?? null;
  // Which other location has this code as its primary (it can be primary at one place only).
  const primaryElsewhere = (code: string) =>
    (allLocationsQuery.data ?? []).find((l) => l.BranchCode === code && l.LocationId !== locationId)?.LocationDesc ?? null;
  function addOther(code: string) {
    if (!code || code === selectedCode || otherCodes.includes(code)) return;
    setValue('OtherBranchCodes', [...otherCodes, code], { shouldDirty: true, shouldValidate: true });
  }
  function removeOther(code: string) {
    setValue('OtherBranchCodes', otherCodes.filter((c) => c !== code), { shouldDirty: true, shouldValidate: true });
  }
  const otherCodeGroups = codeGroups
    .map((g) => ({ ...g, codes: g.codes.filter((c) => c.IsActive && c.Code !== selectedCode && !otherCodes.includes(c.Code)) }))
    .filter((g) => g.codes.length > 0);
  const locationOtherCodes = locationQuery.data?.Location.OtherCodes ?? [];

  return (
    <section className="max-w-3xl">
      <h1 className="font-heading text-2xl font-bold text-navy">{isEdit ? 'Edit location' : 'Add location'}</h1>
      {submitError && <div role="alert" className="mt-4 rounded border border-red/30 bg-red/10 px-3 py-2 text-sm text-red">{submitError}</div>}

      <form onSubmit={handleSubmit((d) => { setSubmitError(null); mutation.mutate(d); })} className="mt-5 space-y-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Location" error={formState.errors.LocationDesc?.message} hint="Name it after the suburb it is in.">
            <input {...register('LocationDesc')} className={inputCls} placeholder="e.g. Pakenham" />
          </Field>
          <Field label="Type"><select {...register('LocationType')} className={inputCls}>{locationTypes.map((t) => <option key={t} value={t}>{locationTypeLabels[t]}</option>)}</select></Field>
          <Field
            label="Primary branch"
            hint={!legacyCode && !(selectedCode && primaryElsewhere(selectedCode)) ? 'Decides the region and area.' : undefined}
          >
            {board ? (
              <select {...register('BranchCode', { onChange: (e) => onPickCode(e.target.value) })} className={inputCls}>
                <option value="">— no code —</option>
                {legacyCode && <option value={legacyCode}>{legacyCode} (not in the hierarchy)</option>}
                {codeGroups.map((g) => (
                  <optgroup key={g.label} label={g.label}>
                    {g.codes.map((c) => <option key={c.Code} value={c.Code}>{c.Code} {c.Description}{c.IsActive ? '' : ' (inactive)'}</option>)}
                  </optgroup>
                ))}
                {unallocatedCodes.length > 0 && (
                  <optgroup label="Unallocated (no area yet)">
                    {unallocatedCodes.map((c) => <option key={c.Code} value={c.Code}>{c.Code} {c.Description}</option>)}
                  </optgroup>
                )}
              </select>
            ) : (
              <input {...register('BranchCode')} className={inputCls} placeholder={codesQuery.isLoading ? 'Loading codes…' : ''} />
            )}
            {legacyCode && (
              <span className="mt-1 block font-normal text-red">
                {legacyCode} is not a reporting branch. Pick one, or add it under Regions, areas &amp; reporting branches.
              </span>
            )}
            {selectedCode && primaryElsewhere(selectedCode) && (
              <span className="mt-1 block font-normal text-red">
                Already the primary branch of {primaryElsewhere(selectedCode)}. Change that location first, or add it below as another branch.
              </span>
            )}
          </Field>
          <Field label="Part of site" error={formState.errors.ParentLocationId?.message}>
            <select {...register('ParentLocationId', { setValueAs: toParentLocationId })} className={inputCls} disabled={hasChildren}>
              <option value="">— top-level site</option>
              {parentOptions.map((l) => <option key={l.LocationId} value={l.LocationId}>{l.LocationDesc}</option>)}
            </select>
            <span className="mt-1 block font-normal text-slate-600">
              {hasChildren
                ? 'This location has sub-locations of its own, so it stays top-level.'
                : 'A branch and a tool store at the same address are two sub-locations of one site.'}
            </span>
          </Field>
          {derived ? (
            <>
              <Derived label="Region" value={derivedRegion?.RegionDesc ?? (derived.AreaId === null ? 'None - the code has no area yet' : '—')} code={selectedCode} />
              <Derived label="Area" value={derivedArea?.AreaDesc ?? '—'} code={selectedCode} />
            </>
          ) : (
            <>
              {/* No reporting branch: pick the AREA; its region comes from the Region → Area
                  hierarchy (Anthony, 25/10/2026), so the two can never disagree. */}
              <Field label="Area">
                <select {...register('AreaId', { setValueAs: toOptionalId, onChange: (e) => onPickArea(e.target.value) })} className={inputCls}>
                  <option value="">—</option>
                  {areaGroups.map((g) => (
                    <optgroup key={g.label} label={g.label}>
                      {g.areas.map((a) => <option key={a.AreaId} value={a.AreaId}>{a.AreaDesc} — {regionName(a.RegionId) ?? 'no region'}</option>)}
                    </optgroup>
                  ))}
                </select>
              </Field>
              <div className="block text-xs font-semibold text-darkGrey">
                Region
                <p className="mt-1 rounded border border-border bg-[#F4F6F9] px-2 py-1.5 text-sm font-normal text-darkGrey" aria-label="Region">
                  {areaRegion?.RegionDesc ?? (selectedArea == null && legacyRegion ? legacyRegion.RegionDesc : '—')}
                </p>
                <span className="mt-1 block font-normal text-slate-600">
                  {selectedArea == null && legacyRegion
                    ? 'Kept from before - pick an area and the region follows it.'
                    : <>From the area. Change it on <Link to="/locations/hierarchy" className="text-navy underline">Regions, areas &amp; reporting branches</Link>.</>}
                </span>
              </div>
            </>
          )}
          <div className="sm:col-span-2">
            <OtherBranches
              codes={otherCodes}
              describe={describe}
              primaryElsewhere={primaryElsewhere}
              groups={otherCodeGroups}
              onAdd={addOther}
              onRemove={removeOther}
              disabled={!board}
              error={formState.errors.OtherBranchCodes?.message}
            />
            {locationOtherCodes.length > 0 && (
              <p className="mt-2 text-xs text-slate-600">
                Finance codes held here:{' '}
                {locationOtherCodes.map((c) => `${c.Code} (${c.CodeType === 'GLMinor' ? 'GL minor' : c.CodeType === 'DebtorsDivision' ? 'debtors division' : c.CodeType})`).join(', ')}
                . Not changed here.
              </p>
            )}
          </div>
          <Field label="OU"><input {...register('OU')} className={inputCls} /></Field>
          <Field label="AD office (officeLocation)"><input {...register('AdOffice')} className={inputCls} /></Field>
        </div>

        <AddressFieldset
          legend="Physical address (provisioned to the user's AD attributes)"
          prefix="Physical"
          register={register}
          hint={selectedParent
            ? `Leave blank to use ${selectedParent.LocationDesc}'s address — one location per physical address.`
            : undefined}
        />
        <PostalFieldset register={register} />

        <fieldset className="rounded border border-border bg-bg/40 p-4">
          <legend className="px-1 text-xs font-semibold text-darkGrey">Branch directory</legend>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Phone" error={formState.errors.Phone?.message}>
              <input {...register('Phone')} className={inputCls} placeholder="07 3022 8900" />
            </Field>
            <Field label="Australia Post delivers to the physical address">
              <select {...register('AustPostDelivers')} className={inputCls}>
                <option value="">— not known —</option>
                {austPostValues.map((v) => <option key={v} value={v}>{austPostLabels[v]}</option>)}
              </select>
            </Field>
            <div className="sm:col-span-2">
              <Field label="Delivery notes, prior and alternate addresses" error={formState.errors.AddressNotes?.message}>
                <textarea {...register('AddressNotes')} rows={2} className={inputCls} placeholder="e.g. use 1435 Attention BIB RTAY" />
              </Field>
            </div>
          </div>
        </fieldset>

        <div className="flex flex-wrap gap-4">
          {derived ? (
            <span className="flex items-center gap-2 text-sm text-darkGrey" title={`From reporting branch ${selectedCode}`}>
              <span className="rounded bg-[#E6ECF2] px-2 py-0.5 text-xs font-semibold text-navy">{derived.IsBranch ? 'Operating branch' : 'Not an operating branch'}</span>
              <span className="rounded bg-[#E6ECF2] px-2 py-0.5 text-xs font-semibold text-navy">{derived.IsMinor ? 'Minor code' : 'Not a minor code'}</span>
            </span>
          ) : (
            <>
              <label className="flex items-center gap-2 text-sm font-medium text-darkGrey"><input type="checkbox" {...register('IsBranch')} /> Is a branch</label>
              <label className="flex items-center gap-2 text-sm font-medium text-darkGrey"><input type="checkbox" {...register('IsMinor')} /> Is a minor code</label>
            </>
          )}
          <label className="flex items-center gap-2 text-sm font-medium text-darkGrey"><input type="checkbox" {...register('IsStockLocation')} /> Stock location</label>
          <label className="flex items-center gap-2 text-sm font-medium text-darkGrey"><input type="checkbox" {...register('Active')} /> Active</label>
        </div>
        <Field label="Notes"><textarea {...register('Notes')} rows={3} className={inputCls} /></Field>

        <div className="flex gap-2">
          <button type="submit" disabled={mutation.isPending} className="rounded bg-orange px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50">
            {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create location'}
          </button>
          <button type="button" onClick={() => navigate('/locations')} className="rounded border border-border bg-white px-4 py-2 text-sm font-medium text-darkGrey hover:bg-bg">Cancel</button>
        </div>
      </form>
    </section>
  );
}

type Reg = UseFormRegister<LocationFormData>;

interface CodeGroup { label: string; codes: { Code: string; Description: string }[] }

/**
 * The location's other branches (team walk-through 26/10/2026: Pakenham runs two
 * branches). Chips with a remove button, and a picker that adds one.
 */
function OtherBranches({ codes, describe, primaryElsewhere, groups, onAdd, onRemove, disabled, error }: {
  codes: string[];
  describe: (code: string) => string | null;
  primaryElsewhere: (code: string) => string | null;
  groups: CodeGroup[];
  onAdd: (code: string) => void;
  onRemove: (code: string) => void;
  disabled: boolean;
  error?: string;
}) {
  return (
    <div className="text-xs font-semibold text-darkGrey">
      <span id="other-branches-label">Other branches at this location</span>
      <ul className="mt-1 flex flex-wrap gap-2" aria-labelledby="other-branches-label">
        {codes.length === 0 && <li className="font-normal text-slate-600">None - only the primary branch.</li>}
        {codes.map((c) => (
          <li key={c} className="inline-flex items-center gap-1 rounded bg-[#E6ECF2] px-2 py-1 text-sm font-normal text-navy">
            <span>{c}{describe(c) ? ` ${describe(c)}` : ''}</span>
            {primaryElsewhere(c) && <span className="text-xs text-darkGrey">(primary at {primaryElsewhere(c)})</span>}
            <button type="button" onClick={() => onRemove(c)} aria-label={`Remove branch ${c}`} className="ml-1 rounded px-1 font-semibold text-navy hover:bg-white">×</button>
          </li>
        ))}
      </ul>
      <select
        aria-label="Add another branch"
        value=""
        disabled={disabled}
        onChange={(e) => onAdd(e.target.value)}
        className={`${inputCls} sm:w-1/2`}
      >
        <option value="">+ Add another branch…</option>
        {groups.map((g) => (
          <optgroup key={g.label} label={g.label}>
            {g.codes.map((c) => <option key={c.Code} value={c.Code}>{c.Code} {c.Description}</option>)}
          </optgroup>
        ))}
      </select>
      {error && <span className="mt-1 block font-normal text-red">{error}</span>}
    </div>
  );
}

function AddressFieldset({ legend, prefix, register, hint }: { legend: string; prefix: 'Physical'; register: Reg; hint?: string }) {
  return (
    <fieldset className="rounded border border-border bg-bg/40 p-4">
      <legend className="px-1 text-xs font-semibold text-darkGrey">{legend}</legend>
      {hint && <p className="mb-3 text-xs text-slate-600">{hint}</p>}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2"><Field label="Street"><input {...register(`${prefix}Street`)} className={inputCls} /></Field></div>
        <Field label="City"><input {...register(`${prefix}City`)} className={inputCls} /></Field>
        <Field label="State"><input {...register(`${prefix}State`)} className={inputCls} /></Field>
        <Field label="Postcode"><input {...register(`${prefix}Postcode`)} className={inputCls} /></Field>
        <Field label="Country"><input {...register(`${prefix}Country`)} className={inputCls} /></Field>
      </div>
    </fieldset>
  );
}

function PostalFieldset({ register }: { register: Reg }) {
  return (
    <fieldset className="rounded border border-border bg-bg/40 p-4">
      <legend className="px-1 text-xs font-semibold text-darkGrey">Postal address</legend>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Street"><input {...register('PostalStreet')} className={inputCls} /></Field>
        <Field label="PO Box"><input {...register('PostOfficeBox')} className={inputCls} /></Field>
        <Field label="City"><input {...register('PostalCity')} className={inputCls} /></Field>
        <Field label="State"><input {...register('PostalState')} className={inputCls} /></Field>
        <Field label="Postcode"><input {...register('PostalPostcode')} className={inputCls} /></Field>
        <Field label="Country"><input {...register('PostalCountry')} className={inputCls} /></Field>
      </div>
    </fieldset>
  );
}

/** A value the chosen code decides; it is changed on the hierarchy, not here. */
function Derived({ label, value, code }: { label: string; value: string; code: string | undefined }) {
  return (
    <div className="block text-xs font-semibold text-darkGrey">
      {label}
      <p className="mt-1 rounded border border-border bg-[#F4F6F9] px-2 py-1.5 text-sm font-normal text-darkGrey">{value}</p>
      <span className="mt-1 block font-normal text-slate-600">
        From reporting branch {code}. Change it on{' '}
        <Link to="/locations/hierarchy" className="text-navy underline">Regions, areas &amp; reporting branches</Link>.
      </span>
    </div>
  );
}

function Field({ label, error, hint, children }: { label: string; error?: string; hint?: string; children: React.ReactNode }) {
  const field = (
    <label className="block text-xs font-semibold text-darkGrey">
      {label}
      {children}
      {error && <span className="mt-1 block font-normal text-red">{error}</span>}
    </label>
  );
  // The hint sits outside the label, so the control's name stays the label alone.
  return hint ? <div>{field}<span className="mt-1 block text-xs text-slate-600">{hint}</span></div> : field;
}
